import { useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { applyTheme, readStoredTheme, storeTheme, type ThemeMode } from '@/lib/theme';
import { cn } from '@/lib/cn';

const OPTIONS: Array<{ mode: ThemeMode; icon: typeof Sun; label: string }> = [
  { mode: 'light', icon: Sun, label: 'Hell' },
  { mode: 'system', icon: Monitor, label: 'System' },
  { mode: 'dark', icon: Moon, label: 'Dunkel' },
];

export function ThemeToggle() {
  const [mode, setMode] = useState<ThemeMode>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(mode);
    storeTheme(mode);
    if (mode !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [mode]);

  return (
    <div
      role="radiogroup"
      aria-label="Farbschema"
      className="inline-flex rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-0.5"
    >
      {OPTIONS.map(({ mode: option, icon: Icon, label }) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={mode === option}
          aria-label={label}
          title={label}
          onClick={() => setMode(option)}
          className={cn(
            'grid h-7 w-8 place-items-center rounded-md transition-colors',
            mode === option
              ? 'bg-[var(--surface)] text-[var(--ink)] shadow-sm'
              : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
          )}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
}

/** Lets charts pick the dark step of a group colour. */
export function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark')),
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}
